import { defineStore } from "pinia";
import FeedService from "@/services/feedService";

export type TabState = "idle" | "syncing" | "complete" | "failed";

export interface FeedFile {
  id: number;
  fileUrl: string;
  name: string;
}

export interface SyncSummary {
  newCount: number;
  updatedCount: number;
  deletedCount: number;
}

interface FeedState {
  brandFile: FeedFile | null;
  productFile: FeedFile | null;
  brandTabState: TabState;
  productTabState: TabState;
  brandSummary: SyncSummary | null;
  productSummary: SyncSummary | null;
  brandRefreshing: boolean;
  productRefreshing: boolean;
}

function mapEffectiveStatus(effectiveStatus: string | undefined): TabState {
  if (effectiveStatus === "syncing") return "syncing";
  if (effectiveStatus === "complete") return "complete";
  if (effectiveStatus === "failed") return "failed";
  return "idle";
}

export const useFeedStore = defineStore("feed", {
  state: (): FeedState => ({
    brandFile: null,
    productFile: null,
    brandTabState: "idle",
    productTabState: "idle",
    brandSummary: null,
    productSummary: null,
    brandRefreshing: false,
    productRefreshing: false,
  }),

  getters: {
    // Derived, not stored - reflects whatever we currently know locally about brand's
    // own state. Brand is the default tab and always cold-loads on mount, so this is
    // accurate before Product can even be reached, with zero extra API calls on every
    // tab switch. The one gap - someone else starting a brand sync while we're already
    // sitting on Product tab without revisiting Brand - is caught by the backend's own
    // gate (see markBrandBusy below), not by polling for it here.
    isBrandBusy: (state): boolean => state.brandTabState === "syncing",
  },

  actions: {
    // Lazy, cached: one lookup call per table name, per business profile session.
    // Reset back to null on business-profile switch by useStoreReset, so this
    // fires again fresh the first time a tab is shown after switching profiles.
    // A cold "complete" is treated as idle and its summary is never fetched here -
    // a stale success from before this session isn't worth surfacing on open; only
    // a live refresh() (during an active session) shows the real completion + counts.
    async ensureFileLoaded(isBrand: boolean) {
      const fileKey = isBrand ? "brandFile" : "productFile";
      const stateKey = isBrand ? "brandTabState" : "productTabState";

      if (this[fileKey]) {
        return; // already loaded for this business profile session
      }

      const feedService = new FeedService();
      const result: any = await feedService.lookupFile(isBrand ? "Brands" : "Products");

      if (!result?.data) {
        this[stateKey] = "idle";
        return;
      }

      this[fileKey] = { id: result.data.id, fileUrl: result.data.fileUrl, name: result.data.name };
      const status = mapEffectiveStatus(result.data.effectiveStatus);
      this[stateKey] = status === "complete" ? "idle" : status;
    },

    // The one call behind "Sync feed ->", for both the first-ever setup and every re-sync after.
    async startSync(isBrand: boolean, sheetUrl: string, name?: string) {
      const fileKey = isBrand ? "brandFile" : "productFile";
      const stateKey = isBrand ? "brandTabState" : "productTabState";

      const feedService = new FeedService();
      const result: any = await feedService.sync({ isBrand, sheetUrl, name });

      this[fileKey] = {
        id: result.data.fileId,
        fileUrl: sheetUrl,
        name: name ?? this[fileKey]?.name ?? "",
      };
      this[stateKey] = "syncing";
    },

    // The one function behind every Refresh click and every dialog reopen. Guarded
    // per-tab so rapid/duplicate clicks can't fire overlapping calls. Only ever
    // writes to that tab's own state/summary, never the other tab's.
    async refresh(isBrand: boolean) {
      const fileKey = isBrand ? "brandFile" : "productFile";
      const stateKey = isBrand ? "brandTabState" : "productTabState";
      const summaryKey = isBrand ? "brandSummary" : "productSummary";
      const refreshingKey = isBrand ? "brandRefreshing" : "productRefreshing";
      const file = this[fileKey];

      if (!file || this[refreshingKey]) {
        return;
      }

      this[refreshingKey] = true;
      try {
        const feedService = new FeedService();
        const result: any = await feedService.getFile(file.id);
        const status = mapEffectiveStatus(result?.data?.effectiveStatus);
        this[stateKey] = status;

        if (status === "complete") {
          const latest: any = await feedService.getLatestResult(file.id);
          this[summaryKey] = latest?.data ?? null;
        }
      } finally {
        this[refreshingKey] = false;
      }
    },

    // Used on Product-tab open and immediately before startSync(false, ...). Ensures we
    // have at least some local knowledge of brand's file so isBrandBusy has something to
    // derive from. In practice this is always a no-op cache hit, since Brand is the
    // default tab and always cold-loads on mount before Product can be reached.
    async checkBrandGate() {
      await this.ensureFileLoaded(true);
    },

    // Called when the backend rejects a product sync with "brand sync in progress" - a
    // narrow race where brandTabState still looked idle locally, but a brand sync started
    // just before the request landed. The rejection itself confirms brand is syncing, so
    // reflect that truth directly - this also makes the Brand tab, if visited, show it.
    markBrandBusy() {
      this.brandTabState = "syncing";
    },
  },
});
